import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    productFilterToRequestBodyConverter,
    useGetProductById,
    useProductsLazy,
} from '@travelity/api';
import { ConfirmationDialog, useMediaDevice } from '@travelity/ui';
import { useTranslation } from 'react-i18next';

import { FilterOption, FilterTypes } from '@components/filters/filters.types';
import { Filters } from '@components/filters';
import {
    List,
    ListItems,
    ListMain,
    ListSidebar,
    ListTitle,
} from '@components/list-layout';
import { useUserContext } from '@contexts/user';
import { useHasFilter } from '@components/filters/filters.hooks';
import { AddButton } from '@components/add-button';
import type { GetProductsResItem2Dto } from '@travelity/api/generated';
import { useLoadOnScroll } from '@/hooks';
import { productTypeOptions } from '../select-type/select-type';
import ProductEditForm from './components/product-edit-form';
import { ProductItemSkeleton } from '../../components/product-item-skeleton';
import { ProductItem } from '../../components/product-item';
import {
    OnboardingClasses,
    OnboardingTours,
    useOnboardingTour,
} from '@/onboarding';
import { Onboarding } from '@/onboarding/onboarding.component';

import PackageItem from './PackageItem/PackageItem';
import { PackageSidebar } from '@/views/products/PackageItem/components/PackageSidebar';

const productFilters: FilterOption[] = [
    {
        name: 'productType',
        label: 'Product Type',
        type: FilterTypes.DROPDOWN,
        selectText: 'Please, select the value for product type',
        options: productTypeOptions,
    },
    {
        name: 'createdAt',
        label: 'Created At',
        type: FilterTypes.DATES,
        disable: used =>
            used.includes('updatedAt') ||
            used.includes('updatedBy') ||
            used.includes('deletedAt') ||
            used.includes('deletedBy'),
        selectText: 'Please, select the dates for created at filter',
    },
    {
        name: 'createdBy',
        label: 'Created By',
        type: FilterTypes.KEYWORD,
        disable: used =>
            used.includes('updatedAt') ||
            used.includes('updatedBy') ||
            used.includes('deletedAt') ||
            used.includes('deletedBy'),
        selectText: 'Type user name or email',
    },
    {
        name: 'updatedAt',
        label: 'Updated At',
        type: FilterTypes.DATES,
        disable: used =>
            used.includes('createdAt') ||
            used.includes('createdBy') ||
            used.includes('deletedAt') ||
            used.includes('deletedBy'),
        selectText: 'Please, select the dates for updated at filter',
    },
    {
        name: 'updatedBy',
        label: 'Updated By',
        type: FilterTypes.KEYWORD,
        disable: used =>
            used.includes('createdAt') ||
            used.includes('createdBy') ||
            used.includes('deletedAt') ||
            used.includes('deletedBy'),
        selectText: 'Type user name or email',
    },
    {
        name: 'deletedAt',
        label: 'Deleted At',
        type: FilterTypes.DATES,
        disable: used =>
            used.includes('createdAt') ||
            used.includes('createdBy') ||
            used.includes('updatedAt') ||
            used.includes('updatedBy'),
        selectText: 'Please, select the dates for deleted at filter',
    },
    {
        name: 'deletedBy',
        label: 'Deleted By',
        type: FilterTypes.KEYWORD,
        disable: used =>
            used.includes('createdAt') ||
            used.includes('createdBy') ||
            used.includes('updatedAt') ||
            used.includes('updatedBy'),
        selectText: 'Type user name or email',
    },
    {
        name: 'searchText',
        label: 'Search',
        type: FilterTypes.SEARCH,
        selectText: 'Search product names, route locations, etc...',
    },
];

function Products() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { productId } = useParams();
    const { onboardingProps, complete } = useOnboardingTour(
        OnboardingTours.INITIAL
    );
    const { onboardingProps: productListOnboardingProps } = useOnboardingTour(
        OnboardingTours.PRODUCT_LIST
    );

    const { user } = useUserContext();
    const individual = user?.workspaces.selected.individual;
    useEffect(() => {
        if (!user?.workspaces.selected.individual && onboardingProps.run) {
            complete();
        }
    }, [individual, onboardingProps.run]);

    const { isMobile } = useMediaDevice();

    // filters
    const [filters, setFilters] = useState({});
    const hasFilters = useHasFilter(filters, productFilters);
    const filterHeight = isMobile ? 0 : hasFilters ? 117 : 61;

    const containerRef = useRef<HTMLElement>();
    const [showInvalidatePagination, setShowInvalidatePagination] =
        useState(false);

    const {
        data: products,
        isLoading,
        refetch,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        totalCount,
    } = useProductsLazy(
        useMemo(() => productFilterToRequestBodyConverter(filters), [filters]),
        {
            onInvalidPagination: () => {
                setShowInvalidatePagination(true);
            },
        }
    );

    const productFromList = useMemo(() => {
        return productId
            ? products?.find(({ id }) => id === productId)
            : undefined;
    }, [products, productId]);

    const shouldFetchProductById =
        !!productId && !isLoading && !productFromList;
    const { data: fetchedProductById, isLoading: isLoadingProduct } =
        useGetProductById(shouldFetchProductById ? productId : undefined);

    const product = productFromList || fetchedProductById;

    const { onScroll } = useLoadOnScroll({
        hasNextPage: !!hasNextPage && !isFetchingNextPage,
        fetchNextPage,
    });

    const showCreate = user?.roleAccess.product?.create;

    return (
        <>
            <List>
                <ListMain
                    isLoading={isLoading}
                    SkeletonComponent={ProductItemSkeleton}
                >
                    <ListTitle
                        title={hasFilters ? '' : 'All Products'}
                        filters={
                            <Filters
                                values={filters}
                                setValues={setFilters}
                                options={productFilters}
                            />
                        }
                    >
                        {showCreate && (
                            <AddButton
                                className={
                                    OnboardingClasses.CREATE_PRODUCT_BUTTON
                                }
                                text={t('Create Product', { ns: 'product' })}
                                onClick={() => navigate('/products/add')}
                            />
                        )}
                    </ListTitle>
                    <ListItems
                        items={totalCount}
                        isLoading={isLoading}
                        scrollbarProps={{
                            onScrollY: onScroll,
                            containerRef: el => {
                                containerRef.current = el;
                            },
                        }}
                        noItemsText="Filter results will be here"
                        entityName="product"
                        entityNamePlural="products"
                        subtractHeight={72 + filterHeight}
                    >
                        {products?.map(p =>
                            p.type === 'dynamic_package' ? (
                                <PackageItem
                                    key={p.id}
                                    product={p as GetProductsResItem2Dto}
                                    isSelected={productId === p.id}
                                />
                            ) : (
                                <ProductItem
                                    key={p.id}
                                    product={p}
                                    isSelected={productId === p.id}
                                    refetch={refetch}
                                />
                            )
                        )}
                        {hasNextPage && <ProductItemSkeleton />}
                    </ListItems>
                </ListMain>
                <ListSidebar
                    placeholderText={
                        isLoadingProduct
                            ? 'Loading...'
                            : 'No product is selected for preview'
                    }
                    isEmpty={!product}
                    onClose={() => navigate('/products')}
                >
                    {product && product.type === 'dynamic_package' && (
                        <PackageSidebar
                            product={product as GetProductsResItem2Dto}
                            onClose={() => navigate('/products')}
                        />
                    )}
                    {product && product.type !== 'dynamic_package' && (
                        <ProductEditForm
                            product={product}
                            onClose={() => navigate('/products')}
                        />
                    )}
                </ListSidebar>
            </List>
            <ConfirmationDialog
                handleConfirm={() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = 0;
                    }
                    refetch();
                    setShowInvalidatePagination(false);
                }}
                open={showInvalidatePagination}
                title="The list of products has been updated!"
                confirmText="Ok"
                content="The list will be reloaded to keep you up to date."
            />
            {individual && <Onboarding {...onboardingProps} />}
            {!!products?.length && !onboardingProps.run && (
                <Onboarding {...productListOnboardingProps} />
            )}
        </>
    );
}

export default Products;
